import gsap from 'gsap'
import EmblaCarousel from 'embla-carousel'
import { Component } from '../core/smpl-components.js'

// -----------------------------------------------------------------------------
// Animation tuning. Everything that moves is configured here — the stylesheet
// only does layout and stacking.
// -----------------------------------------------------------------------------

// Poses, in percent of the card's own size. The front card is centred; the back
// one sits 10% to the right, so after its 0.9 scale (5% off each side) it still
// sticks out 5% past the front card's right edge — visible even when both cards
// have the same shape.
// Blur in px, as on the parked card in the home header.
const FRONT = { xPercent: 0, yPercent: 0, scale: 1, opacity: 1, blur: 0 }
const BACK = { xPercent: 10, yPercent: 0, scale: 0.9, opacity: 1, blur: 12 } // the next card, behind

// How far the travelling card swings out to the left on its way round, in % of its width.
const ARC_DIST = 55

// Embla's scroll duration (its own unit, default 25). Higher is slower.
const DURATION = 20

// Share of the move, at the back end, over which a card's opacity changes. A
// card is fully opaque for the rest, so the two never show through each other
// while they cross — the leaving card only fades once it is already under.
const FADE = 0.3

// Share of the move, at the back end, over which the blur changes — longer than
// the fade, so a card comes into focus on its way forward, as in the header.
const BLUR = 0.7

// Outward swing: zero at both ends with zero velocity, 1 at the apex. See
// header-panels.js for why it is squared.
const arcShape = (t) => Math.sin(t * Math.PI) ** 2

const lerp = (a, b, t) => a + (b - a) * t
// t remapped so the change happens only over the last `share` of the move.
const backEnd = (t, share) => Math.min(Math.max((t - (1 - share)) / share, 0), 1)
const lerpPose = (a, b, t) => ({
	xPercent: lerp(a.xPercent, b.xPercent, t),
	yPercent: lerp(a.yPercent, b.yPercent, t),
	scale: lerp(a.scale, b.scale, t),
	opacity: lerp(a.opacity, b.opacity, backEnd(t, FADE)),
	blur: lerp(a.blur, b.blur, backEnd(t, BLUR)),
})

// -----------------------------------------------------------------------------
// Text-images stack — the images next to the text block.
//
// One card in front, the next one parked behind it. Embla runs on an invisible
// track laid over the cards and supplies drag, snapping, easing and looping; on
// every scroll its position is turned into a pose per card, so dragging scrubs
// the whole move. The swing follows the drag: going forward (drag left), the
// front card swings out to the left and tucks under while the back card comes
// forward; going back (drag right), the previous card swings in from the left
// and lands on top while the front card slides back. Clicking the strip of the
// back card that shows beside the front one also goes forward. With a single image the template
// renders no `.js-text-images` and nothing is set up. Selector: `.js-text-images`.
// -----------------------------------------------------------------------------
export default class extends Component {
	prepare() {
		this.elStack = this.element.querySelector('.text-images__stack')
		this.elDrag = this.element.querySelector('.text-images__drag')
		this.slides = Array.from(this.element.querySelectorAll('.text-images__slide'))
		this.dots = Array.from(this.element.querySelectorAll('.text-images__dot'))

		this.bndOnDotClick = this.onDotClick.bind(this)
		this.bndOnStackClick = this.onStackClick.bind(this)
		this.bndTween = this.tween.bind(this)
		this.bndMeasure = this.measure.bind(this)
		this.bndUpdate = this.update.bind(this)
	}

	mount() {
		if (this.slides.length < 2 || !this.elDrag) return

		this.embla = EmblaCarousel(this.elDrag, { loop: true, align: 'start', duration: DURATION })
		this.embla.on('scroll', this.bndTween)
		this.embla.on('reInit', this.bndMeasure)
		this.embla.on('select', this.bndUpdate)

		this.dots.forEach((el) => el.addEventListener('click', this.bndOnDotClick))
		this.elStack.addEventListener('click', this.bndOnStackClick)

		this.measure()
		this.update()
	}

	unmount() {
		if (!this.embla) return

		this.dots.forEach((el) => el.removeEventListener('click', this.bndOnDotClick))
		this.elStack.removeEventListener('click', this.bndOnStackClick)

		this.embla.destroy()
		this.embla = null

		gsap.set(this.slides, { clearProps: 'all' })
	}

	onDotClick(event) {
		this.go(this.dots.indexOf(event.currentTarget))
	}

	// The drag layer covers the cards, so the click is hit-tested by position:
	// inside the back card but outside the front one is the visible strip. Listened
	// for on the stack, which also catches the part of the strip that hangs past
	// the drag layer. Embla swallows the click that ends a drag, so a drag never
	// lands here.
	onStackClick(event) {
		const inside = (el) => {
			const r = el.getBoundingClientRect()
			return event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom
		}
		const front = this.slides.find((el) => el.classList.contains('is-front'))
		const back = this.slides.find((el) => el.classList.contains('is-back'))

		if (back && inside(back) && !(front && inside(front))) this.go(this.embla.selectedScrollSnap() + 1)
	}

	// Wrapped by hand, so a jump still takes a valid index when Embla turns its
	// own loop off (two slides can be too few for it).
	go(index) {
		const jump = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		this.embla.scrollTo(this.wrap(index), jump)
	}

	wrap(i) {
		const n = this.slides.length
		return ((i % n) + n) % n
	}

	// The track is one viewport wide per slide, so this converts px to slides.
	measure() {
		this.slideSize = this.elDrag.offsetWidth || 1
		this.tween()
	}

	// d is a card's distance from the current position, in slides, wrapped into
	// [-1, n - 1): 0 = front, 1 = back, between them a straight step forward;
	// -1..0 = the swing round the left, leaving the front (or, going back,
	// arriving at it); beyond 1 = fading in behind.
	tween() {
		const n = this.slides.length
		const position = -this.embla.internalEngine().offsetLocation.get() / this.slideSize

		this.slides.forEach((el, i) => {
			const d = ((((i - position + 1) % n) + n) % n) - 1

			const { blur, ...pose } = this.pose(d, n)
			gsap.set(el, { ...pose, filter: `blur(${blur}px)` })
			// Stacking is handed over at the apex of the swing (d = 0.5).
			el.classList.toggle('is-front', Math.abs(d) < 0.5)
			el.classList.toggle('is-back', d >= 0.5 && d < 1.5)
		})
	}

	pose(d, n) {
		if (d >= 0 && d <= 1) return lerpPose(FRONT, BACK, d)

		if (d < 0) {
			const p = -d
			// The swinging card ends up behind the stack. With two cards it is
			// the next back card itself; otherwise it fades out where the new
			// back card fades in.
			const pose = lerpPose(FRONT, n === 2 ? BACK : { ...BACK, opacity: 0 }, p)
			pose.xPercent -= ARC_DIST * arcShape(p)
			return pose
		}

		return { ...BACK, opacity: BACK.opacity * Math.max(0, 2 - d) }
	}

	update() {
		const selected = this.embla.selectedScrollSnap()

		this.dots.forEach((el, i) => {
			el.classList.toggle('is-active', i === selected)
			if (i === selected) el.setAttribute('aria-current', 'true')
			else el.removeAttribute('aria-current')
		})
	}
}
